import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { GlassCard, PageTitle, LuxuryButton } from "@/components/lux";
import { toast } from "sonner";
import { Coins, Trash2, Plus } from "lucide-react";

const inp = "w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white outline-none focus:border-[var(--gold)]/50";
const blank = { coin: "USDT", network: "TRC20", address: "", qr_url: "", min_deposit: 50, instructions: "", enabled: true };

export default function AdminCrypto() {
  const [methods, setMethods] = useState([]);
  const [f, setF] = useState(blank);
  const load = () => api.get("/admin/crypto-settings").then((r) => setMethods(r.data.methods));
  useEffect(() => { load(); }, []);
  const upd = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const onQrFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1024 * 1024) return toast.error("QR image must be under 1MB");
    const reader = new FileReader();
    reader.onload = () => setF((prev) => ({ ...prev, qr_url: reader.result }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    try { await api.post("/admin/crypto-settings", { ...f, min_deposit: Number(f.min_deposit) }); toast.success("Saved"); setF(blank); load(); }
    catch { toast.error("Failed"); }
  };
  const edit = (m) => setF(m);
  const toggle = async (m) => { await api.post("/admin/crypto-settings", { ...m, enabled: !m.enabled }); load(); };
  const del = async (id) => { await api.delete(`/admin/crypto-settings/${id}`); toast.success("Deleted"); load(); };

  return (
    <div className="space-y-6">
      <PageTitle title="Crypto Settings" subtitle="Configure deposit wallets and networks" />
      <div className="grid gap-6 lg:grid-cols-2">
        <GlassCard className="p-6">
          <div className="mb-4 flex items-center gap-2"><Coins size={18} className="text-[var(--gold)]" /><h3 className="font-semibold text-white">{f.id ? "Edit" : "Add"} Method</h3></div>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <input className={inp} placeholder="Coin (USDT)" value={f.coin} onChange={upd("coin")} data-testid="crypto-coin" />
              <input className={inp} placeholder="Network (TRC20)" value={f.network} onChange={upd("network")} data-testid="crypto-network" />
            </div>
            <input className={inp} placeholder="Wallet address" value={f.address} onChange={upd("address")} data-testid="crypto-address" />
            <div className="rounded-xl glass-2 p-3 space-y-2">
              <label className="text-xs font-semibold text-[var(--text-dim)]">Deposit QR Code (upload image or paste a URL)</label>
              <input className={inp} placeholder="QR image URL (optional)" value={f.qr_url?.startsWith("data:") ? "" : f.qr_url} onChange={upd("qr_url")} />
              <input type="file" accept="image/*" onChange={onQrFile} data-testid="crypto-qr-upload" className="block w-full text-xs text-[var(--text-dim)] file:mr-3 file:rounded-lg file:border-0 file:bg-[var(--royal)] file:px-3 file:py-1.5 file:text-white" />
              {f.qr_url && <img src={f.qr_url} alt="QR preview" className="h-28 w-28 rounded-lg border border-white/10 bg-white p-1" data-testid="crypto-qr-preview" />}
            </div>
            <input className={inp} type="number" placeholder="Min deposit" value={f.min_deposit} onChange={upd("min_deposit")} />
            <textarea className={inp} placeholder="Deposit instructions" value={f.instructions} onChange={upd("instructions")} />
            <label className="flex items-center gap-2 text-sm text-[var(--text-dim)]"><input type="checkbox" checked={f.enabled} onChange={(e) => setF({ ...f, enabled: e.target.checked })} data-testid="crypto-enabled" /> Enabled</label>
            <div className="flex gap-2">
              <LuxuryButton onClick={save} data-testid="crypto-save"><Plus size={14} className="mr-1 inline" /> Save Method</LuxuryButton>
              {f.id && <LuxuryButton variant="dark" onClick={() => setF(blank)}>Cancel</LuxuryButton>}
            </div>
          </div>
        </GlassCard>

        <GlassCard className="p-6">
          <h3 className="mb-4 font-semibold text-white">Configured Methods</h3>
          <div className="space-y-3">
            {methods.map((m) => (
              <div key={m.id} className="flex items-center justify-between rounded-xl glass-2 p-4" data-testid={`crypto-item-${m.coin}`}>
                <div>
                  <p className="font-medium text-white">{m.coin} · {m.network}</p>
                  <p className="max-w-[200px] truncate text-xs text-[var(--text-dim)]">{m.address || "No address"}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button onClick={() => toggle(m)} className={`rounded-full border px-2.5 py-1 text-[11px] ${m.enabled ? "border-emerald-400/30 bg-emerald-400/10 text-emerald-300" : "border-white/15 bg-white/5 text-[var(--text-dim)]"}`}>{m.enabled ? "Enabled" : "Disabled"}</button>
                  <button onClick={() => edit(m)} className="text-[var(--gold)] text-xs">Edit</button>
                  <button onClick={() => del(m.id)} className="text-rose-400"><Trash2 size={15} /></button>
                </div>
              </div>
            ))}
            {methods.length === 0 && <p className="text-sm text-[var(--text-dim)]">No methods configured.</p>}
          </div>
        </GlassCard>
      </div>
    </div>
  );
}
